<template>
  <AppNavbar />
  <div class="container-xl px-4 py-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
      <div>
        <h2 class="h4 mb-1">Dashboard</h2>
        <p class="text-muted mb-0">Resumen del negocio — tarjetas con los mismos datos de Reportes.</p>
      </div>
      <div class="btn-group" role="group">
        <button
          v-for="opcion in periodos"
          :key="opcion.id"
          type="button"
          class="btn btn-sm"
          :class="periodo === opcion.id ? 'btn-dark' : 'btn-outline-dark'"
          @click="cambiarPeriodo(opcion.id)"
        >
          {{ opcion.label }}
        </button>
      </div>
    </header>

    <div class="row g-3">
      <div class="col-md-6 col-xl-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <h3 class="h6 d-flex justify-content-between align-items-center">
              Ventas
              <router-link class="small" :to="linkReporte('ReportesVentas')">Ver detalle</router-link>
            </h3>
            <div v-if="tarjetas.ventas.estado === 'cargando'" class="placeholder-glow">
              <span class="placeholder col-6 h3 d-block"></span>
            </div>
            <div v-else-if="tarjetas.ventas.estado === 'error'" class="text-danger small">
              No se pudo cargar este dato.
            </div>
            <div v-else>
              <div class="h3 mb-1">{{ formatoMonto(tarjetas.ventas.datos?.monto_total) }}</div>
              <div class="text-muted small">{{ tarjetas.ventas.datos?.cantidad_ventas || 0 }} ventas</div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <h3 class="h6 d-flex justify-content-between align-items-center">
              Caja
              <router-link class="small" :to="linkReporte('ReportesCierresCaja')">Ver detalle</router-link>
            </h3>
            <div v-if="tarjetas.caja.estado === 'cargando'" class="placeholder-glow">
              <span class="placeholder col-6 h3 d-block"></span>
            </div>
            <div v-else-if="tarjetas.caja.estado === 'error'" class="text-danger small">
              No se pudo cargar este dato.
            </div>
            <div v-else>
              <div class="h3 mb-1">{{ tarjetas.caja.datos?.cantidad_cierres || 0 }}</div>
              <div class="text-muted small" :class="Number(tarjetas.caja.datos?.diferencia_total) !== 0 ? 'text-danger' : ''">
                Diferencia: {{ formatoMonto(tarjetas.caja.datos?.diferencia_total) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <h3 class="h6 d-flex justify-content-between align-items-center">
              Producto más vendido
              <router-link class="small" :to="linkReporte('ReportesProductosVendidos')">Ver detalle</router-link>
            </h3>
            <div v-if="tarjetas.productos.estado === 'cargando'" class="placeholder-glow">
              <span class="placeholder col-8 h5 d-block"></span>
            </div>
            <div v-else-if="tarjetas.productos.estado === 'error'" class="text-danger small">
              No se pudo cargar este dato.
            </div>
            <div v-else-if="!topProducto">
              <div class="text-muted small">Sin ventas en el período.</div>
            </div>
            <div v-else>
              <div class="h6 mb-1">{{ topProducto.nombre_producto }}</div>
              <div class="text-muted small">{{ formatoMonto(topProducto.cantidad_vendida) }} unidades</div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <h3 class="h6 d-flex justify-content-between align-items-center">
              Cuentas por cobrar
              <router-link class="small" :to="{ name: 'ReportesCuentasPorCobrar' }">Ver detalle</router-link>
            </h3>
            <div v-if="tarjetas.cuentas.estado === 'cargando'" class="placeholder-glow">
              <span class="placeholder col-6 h3 d-block"></span>
            </div>
            <div v-else-if="tarjetas.cuentas.estado === 'error'" class="text-danger small">
              No se pudo cargar este dato.
            </div>
            <div v-else>
              <!-- AMB-P2. Hasta el 2026-09-12 esta tarjeta leia
                   `credito_clientes` —el credito de almacen heredado— y
                   mostraba ₲ 0 mientras el colegio tenia 22 millones vencidos
                   en `cr_deudas`. Un reporte vacio se nota; un cero en la
                   pantalla de entrada se cree. -->
              <div class="h3 mb-1" data-test="tarjeta-cuentas-monto">
                {{ formatoMonto(tarjetas.cuentas.datos?.total_pendiente) }}
              </div>
              <div class="text-muted small">
                {{ tarjetas.cuentas.datos?.cantidad_pendientes || 0 }} cuotas —
                <span class="text-danger">{{ formatoMonto(tarjetas.cuentas.datos?.total_vencido) }} vencido</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-6 col-xl-4">
        <div class="card shadow-sm h-100">
          <div class="card-body">
            <h3 class="h6 d-flex justify-content-between align-items-center">
              Compras
              <router-link class="small" :to="linkReporte('ReportesComprasProveedor')">Ver detalle</router-link>
            </h3>
            <div v-if="tarjetas.compras.estado === 'cargando'" class="placeholder-glow">
              <span class="placeholder col-6 h3 d-block"></span>
            </div>
            <div v-else-if="tarjetas.compras.estado === 'error'" class="text-danger small">
              No se pudo cargar este dato.
            </div>
            <div v-else>
              <div class="h3 mb-1">{{ formatoMonto(tarjetas.compras.datos?.monto_total) }}</div>
              <div class="text-muted small">{{ tarjetas.compras.datos?.cantidad_compras || 0 }} compras</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import AppNavbar from '@/components/AppNavbar.vue'
import reportsService from '@/services/reportsService'
import { fechaIsoLocal } from '@/utils/fechaLocal.mjs'

const periodos = [
  { id: 'hoy', label: 'Hoy' },
  { id: 'semana', label: 'Esta semana' },
  { id: 'mes', label: 'Este mes' },
]
const periodo = ref('hoy')

// Mismo criterio que ya usan las vistas de Reportes para sus defaults
// (ver PLAN, punto 3): calculado en el cliente, sin endpoint nuevo.
function calcularRango(id) {
  const hoy = new Date()
  const hoyStr = fechaIsoLocal(hoy)
  if (id === 'hoy') return { fecha_desde: hoyStr, fecha_hasta: hoyStr }
  if (id === 'semana') {
    const diaSemana = hoy.getDay() === 0 ? 7 : hoy.getDay() // lunes=1..domingo=7
    const inicio = new Date(hoy)
    inicio.setDate(hoy.getDate() - (diaSemana - 1))
    return { fecha_desde: fechaIsoLocal(inicio), fecha_hasta: hoyStr }
  }
  // mes
  return { fecha_desde: `${hoyStr.slice(0, 7)}-01`, fecha_hasta: hoyStr }
}

const rango = computed(() => calcularRango(periodo.value))

function estadoInicial() {
  return { estado: 'cargando', datos: null }
}

const tarjetas = ref({
  ventas: estadoInicial(),
  caja: estadoInicial(),
  productos: estadoInicial(),
  cuentas: estadoInicial(),
  compras: estadoInicial(),
})

const topProducto = computed(() => tarjetas.value.productos.datos?.top?.[0] || null)

function linkReporte(name) {
  return { name, query: { fecha_desde: rango.value.fecha_desde, fecha_hasta: rango.value.fecha_hasta } }
}

function formatoMonto(valor) {
  return new Intl.NumberFormat('es-PY', { maximumFractionDigits: 0 }).format(Number(valor) || 0)
}

// Promise.allSettled (no Promise.all): una tarjeta que falla no debe
// bloquear las demás (RNF del SPEC, verificado real en DASH-1V-03).
async function cargar() {
  for (const key of Object.keys(tarjetas.value)) tarjetas.value[key] = estadoInicial()

  const r = rango.value
  const resultados = await Promise.allSettled([
    reportsService.getVentas(r),
    reportsService.getCierresCaja(r),
    reportsService.getProductosVendidos({ ...r, orden: 'desc' }),
    reportsService.getMorosidad({}),
    reportsService.getComprasProveedor(r),
  ])

  const [ventas, caja, productos, cuentas, compras] = resultados

  tarjetas.value.ventas = ventas.status === 'fulfilled'
    ? { estado: 'listo', datos: ventas.value.totales }
    : { estado: 'error', datos: null }

  tarjetas.value.caja = caja.status === 'fulfilled'
    ? { estado: 'listo', datos: caja.value.totales }
    : { estado: 'error', datos: null }

  tarjetas.value.productos = productos.status === 'fulfilled'
    ? { estado: 'listo', datos: { ...productos.value.totales, top: productos.value.filas } }
    : { estado: 'error', datos: null }

  tarjetas.value.cuentas = cuentas.status === 'fulfilled'
    ? { estado: 'listo', datos: cuentas.value.totales }
    : { estado: 'error', datos: null }

  tarjetas.value.compras = compras.status === 'fulfilled'
    ? { estado: 'listo', datos: compras.value.totales }
    : { estado: 'error', datos: null }
}

function cambiarPeriodo(id) {
  periodo.value = id
  cargar()
}

onMounted(cargar)
</script>
